import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AnimationType, SlicePayload, UntypedObject, valueof } from '@/types';
import { CartModel, ConfirmContent, ExproModel, FilterModel } from '@/models';
import type { RootState } from '@/redux/create-store';
import { loadSensefuel } from '@/redux/epics/configuration.epic';
import { subscribeToNewsletter } from '@/redux/epics/newsletter.epics';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { validateDiscountCode } from '@/redux/thunks/checkout/validateDiscountCode';
import { addProductToCartFromCheckout } from '@/redux/thunks/checkout/addProductToCartFromCheckout';
import { deleteProductFromCart } from '@/redux/thunks/deleteProductFromCart';
import { FULL_SCREEN_CONTAINER_MOBILE } from '@/constants/gui';
import { HEADER_COLORS } from '@/constants/header';
import { removeIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/removeIncompatibleProducts';

type GuiModalType = valueof<typeof FULL_SCREEN_CONTAINER_MOBILE>;

export type EmptyItemEspotType = {
  location: string;
  img: string;
  title: string;
  desc: string;
  redirect: {
    url: string;
    description: string;
  }
}

export type buttonModal = {
  label: string;
  action: () => void;
  type?: string;
  id?: string;
}

export type ConfirmationModalData = {
  opened: boolean;
  title: string;
  customContent?: any;
  confirm: buttonModal;
  cancel: buttonModal;
  toTranslate?: boolean;
  fullScreen?: boolean;
  groupVariant?: 'primary' | 'secondary' | 'tertiary';
}

export type GuiState = {
  loginModalVisible: boolean;
  cartModalVisible: boolean;
  boxModalVisible: boolean;
  productModalVisible: boolean;
  alertModalVisible: boolean;
  alertPrimeurInfosModalVisible: boolean;
  alertPrimeurProducts: string[],
  alertPrimeurProductName?: string;
  confirmModalVisible: boolean;
  showPageProgress: boolean;
  alertContent: string;
  alertButton?: string;
  alertErrorCode?: string;
  cartContent?: CartModel;
  showGoToCartBtn: boolean;
  currentBox: UntypedObject;
  boxCustomizationModalVisible: boolean;
  paymentPendingVisible: boolean;
  outOfStockModalVisible: boolean;
  outOfStockProductName: string;
  outOfStockProductPartNumber: string;
  selectGiftModalVisible: boolean;
  summaryVisible: boolean;
  isMobileDevice: boolean;
  isSafari: boolean;
  navBarMenu: UntypedObject[];
  modalVisible: UntypedObject;
  espots: UntypedObject;
  appFooterVisible: boolean;
  appOverlayVisible: boolean;
  headerColor: valueof<typeof HEADER_COLORS>;
  fullScreenContainerMobileAnimation?: AnimationType;
  catalogModalVisible: boolean;
  reloadingOnClick: boolean;
  productDetailsModalWBVisible: boolean;
  productDetailsModalWB: UntypedObject;
  wineboxModalOpen: boolean;
  continueShoppingCallback?: () => void;
  searchOpen: boolean;
  reassuranceVisible: boolean;
  reassurance: UntypedObject;
  productImageZoomVisible: boolean;
  topChronoVisible: boolean;
  deliveryScheduleVisible: boolean;
  shippingId?: string;
  anchor?: string;
  blogMenuModalVisible: boolean;
  blogSubmenuModalVisible: boolean;
  cellarMenuModalVisible: boolean;
  confirmContent?: ConfirmContent;
  isMenuMobileVisible: boolean;
  isFilterMobileVisible: boolean;
  selectedFilterMobile: FilterModel;
  isSortMobileVisible: boolean;
  incompatibleProductsModalVisible: boolean;
  isGoogleBot: boolean;
  storeIdForPrice: string;
  emptyItemEspots: EmptyItemEspotType[];
  addressFormModalVisible: boolean;
  detailsExproModalVisible: boolean;
  selectedExpro: ExproModel;
  contactModalVisible: boolean;
  showAddedProduct: boolean;
  recommendationModalVisible: boolean;
  isCountrySelectorModalVisible: boolean;
  isSelectLanguageModalVisible: boolean;
  confirmationModalData: ConfirmationModalData;
  loadingState: boolean;
}

export const initialState: GuiState = {
  loginModalVisible: false,
  cartModalVisible: false,
  boxModalVisible: false,
  productModalVisible: false,
  alertModalVisible: false,
  alertPrimeurInfosModalVisible: false,
  alertPrimeurProducts: [],
  alertPrimeurProductName: undefined,
  confirmModalVisible: false,
  showPageProgress: false,
  alertContent: '',
  alertButton: undefined,
  alertErrorCode: undefined,
  cartContent: {} as any,
  showGoToCartBtn: false,
  currentBox: {},
  boxCustomizationModalVisible: false,
  paymentPendingVisible: false,
  outOfStockModalVisible: false,
  outOfStockProductName: '',
  outOfStockProductPartNumber: '',
  selectGiftModalVisible: false,
  summaryVisible: true,
  isMobileDevice: false,
  isSafari: false,
  navBarMenu: [],
  modalVisible: {},
  espots: {},
  appFooterVisible: true,
  appOverlayVisible: false,
  headerColor: HEADER_COLORS.default,
  fullScreenContainerMobileAnimation: undefined,
  catalogModalVisible: false,
  reloadingOnClick: false,
  productDetailsModalWBVisible: false,
  productDetailsModalWB: {},
  wineboxModalOpen: false,
  continueShoppingCallback: undefined,
  searchOpen: false,
  reassuranceVisible: false,
  reassurance: {},
  productImageZoomVisible: false,
  topChronoVisible: false,
  deliveryScheduleVisible: false,
  shippingId: undefined,
  anchor: undefined,
  blogMenuModalVisible: false,
  blogSubmenuModalVisible: false,
  cellarMenuModalVisible: false,
  confirmContent: undefined,
  isMenuMobileVisible: false,
  isFilterMobileVisible: false,
  selectedFilterMobile: undefined,
  isSortMobileVisible: false,
  incompatibleProductsModalVisible: false,
  isGoogleBot: false,
  storeIdForPrice: undefined,
  emptyItemEspots: [],
  addressFormModalVisible: false,
  detailsExproModalVisible: false,
  selectedExpro: {} as ExproModel,
  contactModalVisible: false,
  showAddedProduct: false,
  recommendationModalVisible: false,
  isCountrySelectorModalVisible: false,
  isSelectLanguageModalVisible: false,
  confirmationModalData: {
    opened: false,
    title: undefined,
    customContent: undefined,
    confirm: undefined,
    cancel: undefined,
    toTranslate: false,
    fullScreen: false,
    groupVariant: undefined
  },
  loadingState: false
};

const gui = createSlice({
  name: 'gui',
  initialState,
  reducers: {
    showLoginModal: (state) => {
      state.loginModalVisible = true;
    },

    hideLoginModal: (state) => {
      state.loginModalVisible = false;
    },

    showCartModal: (state, action: SlicePayload<{ content: CartModel }>) => {
      state.cartContent = action.payload.content;
      state.cartModalVisible = true;
    },

    hideCartModal: (state) => {
      state.cartContent = undefined;
      state.cartModalVisible = false;
    },

    showProductModal: (state) => {
      state.productModalVisible = true;
    },

    hideProductModal: (state) => {
      state.productModalVisible = false;
    },

    showAlertModal: (
      state,
      action: SlicePayload<{
        content: any, button?: string, showGoToCartBtn?: boolean, currentBox?: any, continueShoppingCallback?: () => void
      }>
    ) => {
      const { content, button, showGoToCartBtn, currentBox, continueShoppingCallback } = action.payload;

      state.alertModalVisible = true;
      state.alertContent = content;
      state.alertButton = button;
      state.continueShoppingCallback = continueShoppingCallback;
      state.currentBox = currentBox;
      state.showGoToCartBtn = showGoToCartBtn;
    },

    hideAlertModal: (state) => {
      state.alertModalVisible = false;
      state.alertContent = undefined;
      state.alertButton = undefined;
      state.alertErrorCode = undefined;
      state.continueShoppingCallback = undefined;
    },

    showRecommendationModal: (
      state,
      action: SlicePayload<{
        content: any, button?: string, showGoToCartBtn?: boolean, currentBox?: any, continueShoppingCallback?: () => void
      }>
    ) => {
      const { content, button, showGoToCartBtn, currentBox, continueShoppingCallback } = action.payload;

      state.recommendationModalVisible = true;
      state.alertContent = content;
      state.alertButton = button;
      state.showGoToCartBtn = showGoToCartBtn;
      state.currentBox = currentBox;
      state.continueShoppingCallback = continueShoppingCallback;
    },

    hideRecommendationModal: (state) => {
      state.recommendationModalVisible = false;
      state.showGoToCartBtn = false;
      state.currentBox = undefined;
    },

    showAlertPrimeurInfosModal: (state, action: SlicePayload<{ products: string[], name: string }>) => {
      const { products, name } = action.payload;
      state.alertPrimeurInfosModalVisible = true;
      state.alertPrimeurProducts = products || [];
      state.alertPrimeurProductName = name;
    },

    hideAlertPrimeurInfosModal: (state) => {
      state.alertPrimeurInfosModalVisible = false;
      state.alertPrimeurProductName = undefined;
      state.alertPrimeurProducts = [];
    },

    showConfirmModal: (state, action: SlicePayload<{ content: any }>) => {
      const { content } = action.payload;
      state.confirmContent = content;
      state.confirmModalVisible = true;
    },

    hideConfirmModal: (state) => {
      state.confirmModalVisible = false;
      state.confirmContent = undefined;
    },

    showPageProgress: (state) => {
      state.showPageProgress = true;
    },

    hidePageProgress: (state) => {
      state.showPageProgress = false;
    },

    showBoxCustomizationModal: (state, action: SlicePayload<{ currentBox?: any }>) => {
      state.currentBox = action.payload.currentBox;
      state.boxCustomizationModalVisible = true;
    },

    hideBoxCustomizationModal: (state) => {
      state.boxCustomizationModalVisible = false;
      state.currentBox = undefined;
    },

    showPaymentPending: (state) => {
      state.paymentPendingVisible = true;
    },

    hidePaymentPending: (state) => {
      state.paymentPendingVisible = false;
    },

    showOutOfStockModal: (state, action: SlicePayload<{ productName: string, productPartNumber: string }>) => {
      const { productName, productPartNumber } = action.payload;
      state.outOfStockProductPartNumber = productPartNumber;
      state.outOfStockProductName = productName;
      state.outOfStockModalVisible = true;
    },

    hideOutOfStockModal: (state) => {
      state.outOfStockProductPartNumber = undefined;
      state.outOfStockProductName = undefined;
      state.outOfStockModalVisible = false;
    },

    showSelectGiftModal: (state) => {
      state.selectGiftModalVisible = true;
    },

    hideSelectGiftModal: (state) => {
      state.selectGiftModalVisible = false;
    },

    setNavBarMenu: (state, action: SlicePayload<{ menu: UntypedObject[] }>) => {
      state.navBarMenu = Array.isArray(action.payload.menu) ? action.payload.menu : [];
    },

    showModal: (state, action: SlicePayload<{ id: string }>) => {
      state.modalVisible[action.payload.id] = true;
    },

    hideModal: (state, action: SlicePayload<{ id: string }>) => {
      state.modalVisible[action.payload.id] = false;
    },

    setIsMobileDevice: (state, action: SlicePayload<{ isMobileDevice: boolean }>) => {
      state.isMobileDevice = action.payload.isMobileDevice;
    },

    setIsSafari: (state, action: SlicePayload<{ isSafari: boolean }>) => {
      state.isSafari = action.payload.isSafari;
    },

    setLayoutEspots: (state, action: SlicePayload<{ espots: UntypedObject }>) => {
      state.espots = action.payload.espots || {};
    },

    showFullScreenContainerMobile: (state, action: SlicePayload<{ modalType: GuiModalType }>) => {
      state[`is${action.payload.modalType}MobileVisible`] = true;
    },

    hideFullScreenContainerMobile: (state, action: SlicePayload<{ modalType: GuiModalType }>) => {
      state[`is${action.payload.modalType}MobileVisible`] = false;
    },

    setFullScreenContainerMobileAnimation: (state, action: SlicePayload<{ animation: AnimationType }>) => {
      state.fullScreenContainerMobileAnimation = action.payload.animation;
    },

    showFullScreen: (
      _state,
      _action: SlicePayload<
        { fullScreenType: GuiModalType, onShow?: () => void, selectedFilter?: FilterModel, isMobile: boolean }
      >
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    ) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    hideFullScreen: (_state, _action: SlicePayload<{ fullScreenType: GuiModalType, onHide?: () => void, isMobile: boolean }>) => {
    },

    setSelectedFilterMobile: (state, action: SlicePayload<{ selectedFilter: FilterModel }>) => {
      state.selectedFilterMobile = action.payload?.selectedFilter;
    },

    showAppFooter: (state) => {
      state.appFooterVisible = true;
    },

    hideAppFooter: (state) => {
      state.appFooterVisible = false;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    searchDropdownOpened: (_state, _action: SlicePayload<{ opened: boolean, isMobile: boolean }>) => {
    },

    showOverlay: (state) => {
      state.appOverlayVisible = true;
    },

    hideOverlay: (state) => {
      state.appOverlayVisible = false;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    desktopMenuOpened: (_state, _action: SlicePayload<{ opened: boolean }>) => {
    },

    setHeaderColor: (state, action: SlicePayload<{ actAsCustomer: any }>) => {
      state.headerColor = action.payload.actAsCustomer ? HEADER_COLORS.highlight : HEADER_COLORS.default;
    },

    showCatalogModal: (state) => {
      state.catalogModalVisible = true;
    },

    hideCatalogModal: (state) => {
      state.catalogModalVisible = false;
    },

    setReloadingOnClick: (state, action: SlicePayload<{ isReloading: boolean }>) => {
      state.reloadingOnClick = action.payload.isReloading;
    },

    showProductDetailsModalWB: (state, action: SlicePayload<{ product: UntypedObject }>) => {
      state.productDetailsModalWB = action.payload.product;
      state.productDetailsModalWBVisible = true;
    },

    hideProductDetailsModalWB: (state) => {
      state.productDetailsModalWB = undefined;
      state.productDetailsModalWBVisible = false;
    },

    toggleWineboxModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.wineboxModalOpen = action.payload.opened;
    },

    openWineboxModal: (state) => {
      state.wineboxModalOpen = true;
    },

    closeWineboxModal: (state) => {
      state.wineboxModalOpen = false;
    },

    openSearch: (state) => {
      state.searchOpen = true;
    },

    closeSearch: (state) => {
      state.searchOpen = false;
    },

    toggleReassuranceModal: (state, action: SlicePayload<{ opened: boolean, reassurance: UntypedObject }>) => {
      const { opened, reassurance } = action.payload;
      state.reassuranceVisible = opened;
      state.reassurance = reassurance;
    },

    toggleProductImageZoomModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.productImageZoomVisible = action.payload.opened;
    },

    toggleTopChronoModal: (state, action: SlicePayload<{ opened: boolean, shippingId: string }>) => {
      const { opened, shippingId } = action.payload;
      state.topChronoVisible = opened;
      state.shippingId = shippingId;
    },

    toggleDeliveryScheduleModal: (state, action: SlicePayload<{ opened: boolean, shippingId: string }>) => {
      const { opened, shippingId } = action.payload;
      state.deliveryScheduleVisible = opened;
      state.shippingId = shippingId;
    },

    setAnchor: (state, action: SlicePayload<{ anchor: string }>) => {
      state.anchor = action.payload.anchor;
    },

    toggleBlogMenuModal: (state, action: SlicePayload<{ show: boolean }>) => {
      state.blogMenuModalVisible = action.payload.show;
    },

    toggleBlogSubmenuModal: (state, action: SlicePayload<{ show: boolean }>) => {
      state.blogSubmenuModalVisible = action.payload.show;
    },

    toggleCellarMenuModal: (state, action: SlicePayload<{ show: boolean }>) => {
      state.cellarMenuModalVisible = action.payload.show;
    },

    openIncompatibleProductsModal: (state) => {
      state.incompatibleProductsModalVisible = true;
    },

    closeIncompatibleProductsModal: (state) => {
      state.incompatibleProductsModalVisible = false;
    },

    setIsGoogleBot: (state, action: SlicePayload<{ isGoogleBot: boolean }>) => {
      state.isGoogleBot = action.payload.isGoogleBot;
    },

    setStoreIdForPrice: (state, action: SlicePayload<{ storeIdForPrice: string }>) => {
      state.storeIdForPrice = action.payload.storeIdForPrice;
    },

    setEmptyItemEspots: (state, action: SlicePayload<{ emptyItemEspots: EmptyItemEspotType[] }>) => {
      state.emptyItemEspots = action.payload.emptyItemEspots;
    },

    toggleAddressFormModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.addressFormModalVisible = action.payload.opened;
    },

    toggleContactModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.contactModalVisible = action.payload.opened;
    },

    toogleCountrySelectorModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.isCountrySelectorModalVisible = action.payload.opened;
    },

    toogleSelectLanguageModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.isSelectLanguageModalVisible = action.payload.opened;
    },

    toggleConfirmationModal: (
      state,
      action: SlicePayload<{
        toTranslate?: boolean;
        opened?: boolean;
        title?: string;
        customContent?: any;
        confirm?: buttonModal;
        cancel?: buttonModal;
        groupVariant?: 'primary' | 'secondary' | 'tertiary';
      }>,
    ) => {
      state.confirmationModalData.opened = action.payload.opened;

      if (!action.payload.opened) {
        state.confirmationModalData.toTranslate = false;
        state.confirmationModalData.title = undefined;
        state.confirmationModalData.customContent = undefined;
        state.confirmationModalData.confirm = undefined;
        state.confirmationModalData.cancel = undefined;
        state.confirmationModalData.groupVariant = undefined;
      } else {
        state.confirmationModalData.toTranslate = action.payload.toTranslate;
        state.confirmationModalData.title = action.payload.title;
        state.confirmationModalData.customContent = action.payload.customContent;
        state.confirmationModalData.confirm = action.payload.confirm;
        state.confirmationModalData.cancel = action.payload.cancel;
        state.confirmationModalData.groupVariant = action.payload.groupVariant;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadSensefuel.pending, (state) => {
      state.showPageProgress = true;
    });

    builder.addCase(loadSensefuel.fulfilled, (state) => {
      state.showPageProgress = false;
    });

    builder.addCase(loadSensefuel.rejected, (state) => {
      state.showPageProgress = false;
    });

    builder.addCase(addProductToCartFromCheckout.rejected, (state, action) => {
      state.alertModalVisible = true;
      state.alertButton = 'product.continueShopping';
      state.alertErrorCode = action.payload.error.errorCode;
    });

    builder.addCase(validateDiscountCode.fulfilled, (state, action: SlicePayload<CartModel>) => {
      const discountTypesToShowModal = ['OFFERED_ITEMS', 'OFFERED_MANY_ITEMS', 'OFFERED_ITEMS_BY_LEVEL'];
      if (discountTypesToShowModal.includes(action.payload.discountCode?.type)) {
        state.selectGiftModalVisible = true;
      }
    });

    builder.addCase(removeIncompatibleProductsFromCheckout.fulfilled, (state) => {
      state.incompatibleProductsModalVisible = false;
    });

    builder.addMatcher(
      (action) => loadingStateActions.true.includes(action.type),
      (state, action: {type: string; meta: {arg: Record<string, string>}}) => {
        if (action.meta.arg.attributeName === 'paiementMode') {
          state.loadingState = false;
          return;
        }

        if (action.type === addProductToCartFromCheckout.pending.type && !action.meta.arg.showLoadingOnButtons) {
          return;
        }

        state.loadingState = true;
      }
    );

    builder.addMatcher(
      (action) => loadingStateActions.false.includes(action.type),
      (state) => {
        state.loadingState = false;
      }
    );
  },
});

const loadingStateActions = {
  true: [
    updateCartAttributeByName.pending.type,
    addProductToCartFromCheckout.pending.type,
    subscribeToNewsletter.pending.type
  ],
  false: [
    updateCartAttributeByName.fulfilled.type,
    updateCartAttributeByName.rejected.type,
    addProductToCartFromCheckout.fulfilled.type,
    addProductToCartFromCheckout.rejected.type,
    subscribeToNewsletter.fulfilled.type,
    subscribeToNewsletter.rejected.type
  ],
};

export const {
  showLoginModal, hideLoginModal,
  showCartModal, hideCartModal,
  showProductModal, hideProductModal,
  showAlertModal, hideAlertModal,
  showRecommendationModal, hideRecommendationModal,
  showAlertPrimeurInfosModal, hideAlertPrimeurInfosModal,
  showConfirmModal, hideConfirmModal,
  showPageProgress, hidePageProgress,
  showBoxCustomizationModal, hideBoxCustomizationModal,
  showPaymentPending, hidePaymentPending,
  showOutOfStockModal, hideOutOfStockModal,
  showSelectGiftModal, hideSelectGiftModal,
  setNavBarMenu,
  showModal, hideModal,
  setIsMobileDevice, setIsSafari, setLayoutEspots,
  showFullScreenContainerMobile, hideFullScreenContainerMobile,
  setFullScreenContainerMobileAnimation,
  showFullScreen, hideFullScreen,
  setSelectedFilterMobile,
  showAppFooter, hideAppFooter,
  searchDropdownOpened,
  showOverlay, hideOverlay,
  desktopMenuOpened,
  setHeaderColor,
  showCatalogModal, hideCatalogModal,
  setReloadingOnClick,
  showProductDetailsModalWB, hideProductDetailsModalWB,
  toggleWineboxModal, openWineboxModal, closeWineboxModal,
  openSearch, closeSearch,
  toggleReassuranceModal, toggleProductImageZoomModal, toggleTopChronoModal,
  toggleDeliveryScheduleModal,
  setAnchor, toggleBlogMenuModal, toggleBlogSubmenuModal, toggleCellarMenuModal,
  openIncompatibleProductsModal, closeIncompatibleProductsModal,
  setIsGoogleBot, setEmptyItemEspots,
  setStoreIdForPrice,
  toggleAddressFormModal,
  toggleContactModal,
  toogleCountrySelectorModal, toogleSelectLanguageModal,
  toggleConfirmationModal
} = gui.actions;

export default gui.reducer;

const guiState = (state: RootState) => state.gui;

export const selectGuiLoadingState = createSelector(
  [guiState],
  (gui) => gui.loadingState
);
